import { AgColumnDefinition, AgGridColumnType, AgGridExportType } from '@/shared/models/ag-grid.model'
import i18n from '@/lang/i18n'
import { AppliedBillableConceptResponse } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.dto'
import { BillableDeliveryNoteStatus } from '@/api/services/billing/common-delivery-note/billing-common-delivery-note.dto'
import { EditableCallbackParams, NewValueParams } from '@ag-grid-community/core/dist/cjs/entities/colDef'

const canEditAutoInvoiceSystem =
    ({ data }: EditableCallbackParams) => {
        const appliedBillableConcept = data as AppliedBillableConceptResponse
        if (appliedBillableConcept.deliveryNote) {
            return [BillableDeliveryNoteStatus.GENERATED, BillableDeliveryNoteStatus.ISSUED]
                .includes(appliedBillableConcept.deliveryNote.status.code)
        }
        return false
    }

export const billingModalDeliveryNotesCrudGridColumnDefs =
    (): Array<AgColumnDefinition> => [
        { type: AgGridColumnType.CUSTOM_CHECKBOX },
        {
            headerName: i18n.tc('accounting.field.concept'),
            field: 'concept.name',
            type: AgGridColumnType.TEXT,
            exportType: AgGridExportType.TEXT,
            minWidth: 200,
            flex: 1,
            suppressSizeToFit: true
        },
        {
            headerName: i18n.tc('accounting.field.client'),
            field: 'customer.name',
            type: AgGridColumnType.TEXT,
            exportType: AgGridExportType.TEXT,
            minWidth: 150,
            flex: 1,
            suppressSizeToFit: true
        },
        {
            headerName: i18n.tc('accounting.field.alias'),
            field: 'alias',
            type: AgGridColumnType.TEXT,
            exportType: AgGridExportType.TEXT,
            minWidth: 150,
            suppressSizeToFit: true
        },
        {
            headerName: i18n.tc('accounting.field.rate'),
            field: 'rate.name',
            type: AgGridColumnType.TEXT,
            exportType: AgGridExportType.TEXT,
            minWidth: 150,
            flex: 1,
            suppressSizeToFit: true
        },
        {
            headerName: i18n.tc('accounting.field.value'),
            field: 'value',
            type: AgGridColumnType.DECIMAL,
            exportType: AgGridExportType.DOUBLE,
            minWidth: 100,
            flex: 1,
            suppressSizeToFit: true,
            editable: ({ data }: EditableCallbackParams) => {
                const appliedBillableConcept = data as AppliedBillableConceptResponse
                return appliedBillableConcept.deliveryNote?.status.code === BillableDeliveryNoteStatus.GENERATED
            },
            cellEditor: 'numericCellEditor',
            cellEditorParams: () => ({ min: 1, step: 0.01 })
        },
        {
            headerName: i18n.tc('accounting.field.caseNumber'),
            field: 'caseStudy.casCasenumber',
            type: AgGridColumnType.TEXT,
            exportType: AgGridExportType.TEXT,
            minWidth: 100,
            suppressSizeToFit: true
        },
        {
            headerName: i18n.tc('global.status'),
            field: 'caseStudy.status',
            type: AgGridColumnType.COMMON_CASE_STATUS,
            cellRendererParams: {
                labelField: 'translatedName'
            },
            exportField: 'caseStudy.status.translatedName',
            exportType: AgGridExportType.TEXT
        },
        {
            headerName: i18n.tc('global.regime'),
            field: 'caseStudy.regime.name',
            type: AgGridColumnType.TEXT,
            exportType: AgGridExportType.TEXT,
            minWidth: 100,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            headerName: i18n.tc('accounting.field.elementID'),
            field: 'entityLabel',
            type: AgGridColumnType.TEXT,
            exportType: AgGridExportType.TEXT,
            minWidth: 100,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        },
        {
            headerName: i18n.tc('accounting.field.error'),
            field: 'autoInvoiceSystemInError',
            type: AgGridColumnType.CHECK_ICON,
            exportType: AgGridExportType.BOOLEAN,
            minWidth: 80,
            suppressSizeToFit: true,
            editable: canEditAutoInvoiceSystem,
            cellEditor: 'checkIconCellEditor',
            onCellValueChanged: ({ data, newValue, api }: NewValueParams) => {
                const appliedBillableConcept = data as AppliedBillableConceptResponse
                if (!newValue) {
                    appliedBillableConcept.autoInvoiceSystemOperationCode = ''
                    appliedBillableConcept.autoInvoiceSystemErrorDescription = ''
                    api.refreshCells()
                }
            }
        },
        {
            headerName: i18n.tc('accounting.field.errorDescription'),
            field: 'autoInvoiceSystemErrorDescription',
            type: AgGridColumnType.TEXT,
            exportType: AgGridExportType.TEXT,
            minWidth: 120,
            suppressSizeToFit: true,
            editable: canEditAutoInvoiceSystem
        },
        {
            headerName: i18n.tc('accounting.field.operationNumber'),
            field: 'autoInvoiceSystemOperationCode',
            type: AgGridColumnType.TEXT,
            exportType: AgGridExportType.TEXT,
            minWidth: 120,
            suppressSizeToFit: true,
            editable: canEditAutoInvoiceSystem
        },
        {
            headerName: i18n.tc('accounting.field.isCopayment'),
            field: 'isCopayment',
            type: AgGridColumnType.CHECK_ICON,
            exportType: AgGridExportType.BOOLEAN,
            minWidth: 80,
            suppressSizeToFit: true
        },
        {
            headerName: i18n.tc('accounting.field.hasCopayment'),
            field: 'hasCopayments',
            type: AgGridColumnType.CHECK_ICON,
            exportType: AgGridExportType.BOOLEAN,
            minWidth: 80,
            suppressSizeToFit: true
        },
        {
            headerName: i18n.tc('accounting.field.copaymentType'),
            field: 'copaymentType',
            type: AgGridColumnType.BILLING_COPAYMENT_TYPE,
            exportField: 'copaymentType.translatedName',
            exportType: AgGridExportType.TEXT,
            minWidth: 80,
            suppressSizeToFit: true
        }
    ]
